import { ICourse } from '@sdk/contracts';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { useMemo } from 'react';

import { useCourseDetail } from '../courses';

import { useMySubscriptions } from './useMySubscriptions';

import { useCookies } from '@/hooks';

const today = dayjs();
const SEEN_MATERIAL_COOKIE = 'checkedMaterial';

enum EMaterialType {
  ACTIVITY = 'ACTIVITY',
  MATERIAL = 'MATERIAL'
}

type INewMaterial = {
  [EMaterialType.ACTIVITY]: string[];
  [EMaterialType.MATERIAL]: string[];
};

const isNewerThanSevenDays = (createdAt: Date) => {
  const date = dayjs(createdAt);
  const diffDays = today.diff(date, 'day');
  return diffDays <= 7;
};

const isUnseenAndNew = (item: { _id: string; createdAt: Date }, seen: string[]) => {
  return isNewerThanSevenDays(item.createdAt) && !seen.includes(item._id);
};

const newMaterialIds = (seen: string[], course: ICourse | undefined): INewMaterial => {
  const newMaterial: INewMaterial = {
    [EMaterialType.ACTIVITY]: [],
    [EMaterialType.MATERIAL]: []
  };
  if (!course) return newMaterial;
  course.sections.forEach(section => {
    section.activities.forEach(activity => {
      if (isUnseenAndNew(activity, seen)) {
        newMaterial[EMaterialType.ACTIVITY].push(activity._id);
      }
    });
  });

  course.foldersWithMaterial.forEach(({ material = [] }) => {
    material.forEach(file => {
      if (isUnseenAndNew(file, seen)) {
        newMaterial[EMaterialType.MATERIAL].push(file._id);
      }
    });
  });
  return newMaterial;
};

export const useNewMaterialChecker = () => {
  const { selectedSubscription } = useMySubscriptions();
  const { course } = useCourseDetail(selectedSubscription?.course?._id);

  const [seenMaterial, setSeenMaterial] = useCookies(
    SEEN_MATERIAL_COOKIE,
    Cookies.get(SEEN_MATERIAL_COOKIE) || '[]'
  );

  const newMaterial = useMemo(() => newMaterialIds(seenMaterial, course), [course, seenMaterial]);

  const isNew = (_id: string, type: keyof typeof EMaterialType) => {
    if (!course) return false;
    return newMaterial[type].includes(_id);
  };

  const markAsSeen = (_id: string) => {
    if (!seenMaterial.includes(_id)) {
      setSeenMaterial([...seenMaterial, _id]);
    }
  };

  const hasSectionNewStuff = (sectionId: string) => {
    const { activities } = course?.sections.find(({ _id }) => _id === sectionId) || {};
    return activities?.some(({ _id }) => isNew(_id, EMaterialType.ACTIVITY));
  };

  const hasCourseNewMaterial = useMemo(() => {
    return newMaterial[EMaterialType.MATERIAL].length > 0;
  }, [newMaterial]);

  return {
    newMaterial,
    hasCourseNewMaterial,
    hasSectionNewStuff,
    markAsSeen,
    isNew
  };
};
